import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import {
  Table,
  Spin,
  Tag,
  Button,
  Input,
  DatePicker,
  Drawer,
  Select,
  Space,
} from "antd";
import {
  Package,
  Download,
  SlidersHorizontal,
  RotateCcw,
  Warehouse,
} from "lucide-react";
import axios from "axios";
import { SERVER_API } from "../../Constant";
import { downloadExcel } from "../../lib/excelGenerator";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "available", label: "Available" },
  { value: "sold", label: "Sold" },
  { value: "returned", label: "Returned" },
  { value: "reorder", label: "Re-order" },
  { value: "location", label: "Location" },
];

export const StockReports = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    "stock_reports" as any,
    ["admin", "manager"],
  );

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [type, setType] = useState("available");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dealerId, setDealerId] = useState<number | undefined>(undefined);
  const [dealers, setDealers] = useState<any[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftType, setDraftType] = useState("available");
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftDealerId, setDraftDealerId] = useState<number | undefined>(undefined);

  useEffect(() => {
    axios
      .get(`${SERVER_API}/Dealers/fetch_dealers.php`, {
        params: { page: 1, limit: 1000 },
      })
      .then((res) => setDealers(res.data.dealerData || []))
      .catch((err) => console.error("Failed to load dealers", err));
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_API}/Reports/fetch_stock_reports.php`,
        {
          params: {
            type,
            search: searchQuery,
            ...(startDate && endDate ? { startDate, endDate } : {}),
            ...(dealerId ? { dealer_id: dealerId } : {}),
          },
        },
      );
      if (response.data.success) {
        setReportData(response.data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
  }, [type, startDate, endDate, dealerId, searchQuery]);

  const openDrawer = () => {
    setDraftType(type);
    setDraftRange(
      startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null,
    );
    setDraftDealerId(dealerId);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    setType(draftType);
    setStartDate(draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "");
    setEndDate(draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "");
    setDealerId(draftDealerId);
    setSearchQuery("");
    setDrawerOpen(false);
  };

  const resetFilters = () => {
    setDraftType("available");
    setDraftRange(null);
    setDraftDealerId(undefined);
    setType("available");
    setStartDate("");
    setEndDate("");
    setDealerId(undefined);
    setSearchQuery("");
    setDrawerOpen(false);
  };

  const activeDealerName = dealers.find((d) => d.id === dealerId)?.name || "";
  const statusLabel =
    STATUS_OPTIONS.find((s) => s.value === type)?.label || type;
  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const isListType = ["available", "sold", "returned"].includes(type);
  const filterSummary = [
    statusLabel,
    isListType ? periodLabel : "",
    activeDealerName,
  ].filter(Boolean).join(" | ");

  const handleDownloadExcel = () => {
    if (reportData.length === 0) return;

    let headers: string[] = [];
    let data: any[][] = [];

    if (isListType) {
      headers = [
        "Type",
        "Model",
        "Engine No",
        "Chassis No",
        "Color",
        "Dealer",
        type === "sold"
          ? "Sold Date"
          : type === "returned"
            ? "Return Date"
            : "Added Date",
      ];
      data = reportData.map((r) => [
        r.v_type,
        r.v_modal,
        r.engine_number,
        r.chassis_number,
        r.color_name,
        r.dealer_name,
        type === "sold"
          ? r.sold_date
          : type === "returned"
            ? r.return_date
            : r.added_date,
      ]);
    } else if (type === "reorder") {
      headers = ["Type", "Model", "Reorder Level", "Current Stock"];
      data = reportData.map((r) => [
        r.v_type,
        r.v_modal,
        r.reorder_level,
        r.current_stock,
      ]);
    } else if (type === "location") {
      headers = ["Location", "Type", "Model", "Quantity"];
      data = reportData.map((r) => [r.location, r.v_type, r.v_modal, r.count]);
    }

    const title = `${statusLabel} Vehicle Stock Report${filterSummary ? ` (${filterSummary})` : ""}`;
    downloadExcel(
      title,
      headers,
      data,
      `Vehicle_Stock_${type}_Report${startDate && endDate ? `_${startDate}_${endDate}` : ""}${activeDealerName ? `_${activeDealerName.replace(/\s+/g, "_")}` : ""}`,
      undefined,
      undefined,
      undefined,
      `${reportData.length} Records`,
    );
  };

  if (permissionLoading) return <LoadingScreen messageQueue={"Loading..."} />;
  if (!hasPermission) return <NoAccessScreen />;

  const columns: any = {
    available: [
      { title: "Type", dataIndex: "v_type", key: "v_type" },
      { title: "Model", dataIndex: "v_modal", key: "v_modal" },
      { title: "Engine No", dataIndex: "engine_number", key: "engine_number" },
      {
        title: "Chassis No",
        dataIndex: "chassis_number",
        key: "chassis_number",
      },
      { title: "Color", dataIndex: "color_name", key: "color_name" },
      {
        title: "Dealer",
        dataIndex: "dealer_name",
        key: "dealer_name",
        render: (name: string) => name ? <Tag color="blue">{name}</Tag> : "-",
      },
      { title: "Added Date", dataIndex: "added_date", key: "added_date" },
    ],
    sold: [
      { title: "Type", dataIndex: "v_type", key: "v_type" },
      { title: "Model", dataIndex: "v_modal", key: "v_modal" },
      { title: "Engine No", dataIndex: "engine_number", key: "engine_number" },
      {
        title: "Chassis No",
        dataIndex: "chassis_number",
        key: "chassis_number",
      },
      {
        title: "Dealer",
        dataIndex: "dealer_name",
        key: "dealer_name",
        render: (name: string) => name ? <Tag color="blue">{name}</Tag> : "-",
      },
      { title: "Sold Date", dataIndex: "sold_date", key: "sold_date" },
    ],
    returned: [
      { title: "Type", dataIndex: "v_type", key: "v_type" },
      { title: "Model", dataIndex: "v_modal", key: "v_modal" },
      { title: "Engine No", dataIndex: "engine_number", key: "engine_number" },
      {
        title: "Chassis No",
        dataIndex: "chassis_number",
        key: "chassis_number",
      },
      {
        title: "Dealer",
        dataIndex: "dealer_name",
        key: "dealer_name",
        render: (name: string) => name ? <Tag color="blue">{name}</Tag> : "-",
      },
      { title: "Return Date", dataIndex: "return_date", key: "return_date" },
      { title: "Reason", dataIndex: "return_reason", key: "return_reason" },
    ],
    reorder: [
      { title: "Type", dataIndex: "v_type", key: "v_type" },
      { title: "Model", dataIndex: "v_modal", key: "v_modal" },
      {
        title: "Reorder Level",
        dataIndex: "reorder_level",
        key: "reorder_level",
      },
      {
        title: "Current Stock",
        dataIndex: "current_stock",
        key: "current_stock",
        render: (stock: number) => <Tag color="red">{stock}</Tag>,
      },
    ],
    location: [
      { title: "Location", dataIndex: "location", key: "location" },
      { title: "Type", dataIndex: "v_type", key: "v_type" },
      { title: "Model", dataIndex: "v_modal", key: "v_modal" },
      {
        title: "Quantity",
        dataIndex: "count",
        key: "count",
        render: (c: number) => <Tag color="green">{c}</Tag>,
      },
    ],
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Vehicle Stock Reports</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm flex flex-col md:flex-row gap-8 justify-between items-start md:items-center">
        <div className="flex flex-col md:flex-row items-center gap-6 w-full">
          <div className="w-full md:w-80">
            <Input
              size="large"
              placeholder="Search Engine / Chassis No..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              prefix={<Package className="w-4 h-4 text-blue-500 mr-2" />}
              className="rounded-xl border-gray-100 bg-gray-50/50 focus:bg-white transition-all shadow-sm"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Warehouse className="w-4 h-4 text-emerald-600" />
            <span>Showing: </span>
            <span className="font-medium text-gray-700">
              {filterSummary || "All Available Vehicles"}
            </span>
          </div>
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col p-6">
          <Table
            dataSource={reportData}
            columns={columns[type]}
            loading={loading}
            rowKey={(record, index) => record.id || index || ""}
            bordered={false}
            pagination={false}
            scroll={{ x: "max-content", y: "calc(100vh - 320px)" }}
          />
        </div>
      </div>

      <Drawer
        title="Stock Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
            <Button
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </Button>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Vehicle Status
            </label>
            <Select
              className="w-full"
              value={draftType}
              onChange={setDraftType}
            >
              {STATUS_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Dealer / Location
            </label>
            <Select
              className="w-full"
              placeholder="All Dealers"
              allowClear
              value={draftDealerId}
              onChange={setDraftDealerId}
              showSearch
              optionFilterProp="children"
            >
              {dealers.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};